import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';

import { clearToken, readToken } from '../auth/token';
import { graphqlEndpoint } from '../config';
import { currentLocale } from '../i18n';
import { isUnauthenticated } from './errors';
import { sessionExpired } from './session';

const httpLink = new HttpLink({ uri: graphqlEndpoint });

/**
 * Read from storage per request rather than captured once: the token changes on
 * sign-in and on sign-out, and a client rebuilt around each of those would
 * throw the cache away with it.
 */
const authLink = new SetContextLink((prevContext) => {
  const headers = (prevContext.headers ?? {}) as Record<string, string>;
  const token = readToken();

  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

/**
 * The language the console is being drawn in, on every operation. The API
 * answers `Accept-Language` first (before the account's own `locale`), so this
 * is what makes a server error land in the same language as the form it is
 * shown under. Read per request, like the token: the switch in the header
 * changes it without rebuilding the client.
 */
const localeLink = new SetContextLink((prevContext) => {
  const headers = (prevContext.headers ?? {}) as Record<string, string>;

  return { headers: { ...headers, 'accept-language': currentLocale() } };
});

/**
 * A stale or missing token is the one error every screen would otherwise have
 * to handle, so it is handled once: throw the token away and let the app show
 * the login page. Nothing is retried -- the operation still fails, and the
 * screen that asked for it simply unmounts.
 */
const sessionLink = new ErrorLink(({ error }) => {
  if (!isUnauthenticated(error)) return;

  clearToken();
  sessionExpired();
});

export function createApolloClient(): ApolloClient {
  return new ApolloClient({
    // Order matters: the auth link has to run before the request leaves, and
    // the session link has to see what came back.
    link: ApolloLink.from([sessionLink, authLink, localeLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        // Neither page type has an id of its own -- they are a window onto a
        // list, and normalising them would merge every page into one entry.
        AdminUserPage: { keyFields: false },
        AdminStoryPage: { keyFields: false },
        // The id of a catalogue row is unique only within its collection:
        // `characters/forest` and `places/forest` are different rows.
        AdminCatalogItem: { keyFields: ['collection', 'id'] },
        AdminStoryStatusCount: { keyFields: false },
        // One row, always the same one.
        AdminStats: { keyFields: [] },
      },
    }),
    devtools: { enabled: import.meta.env.DEV },
  });
}

export const apolloClient = createApolloClient();
